import React, { useState, useEffect } from 'react';
import { Typography, Card, Spin, Alert, Row, Col, Image, Button, Input, Tag, Space } from 'antd';
import { getFromCacheE, saveExpiryToCache, saveToCache } from '../utils/cacheUtils';
import activityTracker from '../user/ActivityTracker';
import { logger } from '../utils/logger';
import { useCompany } from '../context/CompanyContext';
import { useLocation } from 'react-router-dom';
import { RobotOutlined, CloseOutlined } from '@ant-design/icons';
import { callAIProxyStream } from '../api/aiProxy';
import AIUpgradePrompt from '../components/AIUpgradePrompt';
import { useAILimit } from '../user/AIButtonLimitChecker';
import usePageTracking from '../utils/usePageTracking';

const { Title, Text } = Typography;

function ListNews({ initialSymbol }) {
  // GA4 页面追踪
  usePageTracking('新聞列表');
  
  const { companyInfo, updateCompanyInfo } = useCompany();
  const { checkAILimit, showUpgradePrompt, setShowUpgradePrompt, limitInfo } = useAILimit();
  const location = useLocation();
  const [newsData, setNewsData] = useState({
    isLoading: false,
    error: null,
    news: []
  });
  const [analysisResult, setAnalysisResult] = useState({
    isLoading: false,
    error: null,
    result: '',
    isVisible: false,
    type: '' // 'rise', 'fall', 'both'
  });

  // 分析配置狀態
  const [selectedTime, setSelectedTime] = useState('');
  const [selectedType, setSelectedType] = useState('');
  // 使用者自訂問題
  const [userQuestion, setUserQuestion] = useState('');
  

  // 處理 URL 參數中的 symbol
  useEffect(() => {
    const searchParams = new URLSearchParams(location.search);
    const urlSymbol = searchParams.get('symbol');
    
    // 優先使用 URL 參數，其次是 initialSymbol，最後是 companyInfo
    const symbolToUse = urlSymbol || initialSymbol || companyInfo?.symbol;
    
    if (symbolToUse && symbolToUse !== companyInfo?.symbol) {
      logger.log('ListNews: 從 URL 參數或 initialSymbol 獲取到新的 symbol:', symbolToUse);
      
      // 更新 CompanyContext
      updateCompanyInfo({
        symbol: symbolToUse,
        company_name: '',
        sector: '',
        industry: '',
        image: ''
      });
      
      logger.log('ListNews: 已更新 symbol 到 CompanyContext');
    }
  }, [location.search, initialSymbol, companyInfo?.symbol, updateCompanyInfo]);

  // 當 companyInfo.symbol 變化時自動加載數據
  useEffect(() => {
    if (companyInfo?.symbol) {
      logger.log('list_News Symbol 已变化到:', companyInfo.symbol);
      
      // 嘗試從單一快取載入（新聞 + 分析）
      const cacheKey = `list_news_data_${companyInfo.symbol}`;
      logger.log(`嘗試讀取緩存: ${cacheKey}`);
      try {
        const cachedData = getFromCacheE(cacheKey);
        if (cachedData && cachedData.symbol === companyInfo.symbol) {
          logger.log('從緩存加載到符合當前Symbol的數據');
          setNewsData(prevData => ({
            ...prevData,
            news: cachedData.news || [],
            error: null,
            isLoading: false
          }));
          if (cachedData.analysis && cachedData.analysis.html) {
            setAnalysisResult({
              isLoading: false,
              error: null,
              result: cachedData.analysis.html,
              isVisible: true,
              type: cachedData.analysis.time && cachedData.analysis.type
                ? `${cachedData.analysis.time}_${cachedData.analysis.type}`
                : (cachedData.analysis.type || '')
            });
          }
          return; // 命中快取直接返回
        }
      } catch (err) {
        logger.error('讀取緩存時出錯:', err);
      }

      // 如未命中，且列表為空再抓取
      if (newsData.news.length === 0) {
        fetchNewsData();
      }
    }
  }, [companyInfo?.symbol]);

  // 自動載入分析快取（當 symbol 變更時）
  useEffect(() => {
    const symbol = companyInfo?.symbol;
    if (!symbol) return;

    const unifiedKey = `list_news_data_${symbol}`;
    // 只需要 symbol 匹配，有緩存就直接顯示
    try {
      const cachedUnified = getFromCacheE(unifiedKey);
      if (cachedUnified && 
          cachedUnified.symbol === symbol && 
          cachedUnified.analysis && 
          cachedUnified.analysis.html) {
        setAnalysisResult({
          isLoading: false,
          error: null,
          result: cachedUnified.analysis.html || '',
          isVisible: true,
          type: cachedUnified.analysis.time && cachedUnified.analysis.type 
            ? `${cachedUnified.analysis.time}_${cachedUnified.analysis.type}`
            : (selectedTime && selectedType ? `${selectedTime}_${selectedType}` : '')
        });
      }
    } catch (e) {}
  }, [companyInfo?.symbol]);

  // 記錄頁面訪問
  useEffect(() => {
    if (companyInfo?.symbol) {
      activityTracker.trackPageView(companyInfo.symbol);
    }
  }, [companyInfo?.symbol]);

  // 獲取新聞數據
  const fetchNewsData = async () => {
    const selectedSymbol = companyInfo?.symbol;

    if (!selectedSymbol) {
      setNewsData(prev => ({
        ...prev,
        error: '請選擇公司',
        news: []
      }));
      return;
    }

    setNewsData(prev => ({
      ...prev,
      isLoading: true,
      error: null
    }));

    try {
      // 調用 Financial Modeling Prep API
      const response = await fetch(
        `https://financialmodelingprep.com/stable/news/stock?symbols=${selectedSymbol}&apikey=f4SaAaGfr9mSmFULmZuxK1TSomgVkGG2`
      );

      if (!response.ok) {
        throw new Error(`HTTP error! status: ${response.status}`);
      }

      const data = await response.json();
      
      // 過濾和處理新聞數據
      const processedNews = data
        .filter(news => news.symbol === selectedSymbol)
        .map(news => ({
          ...news,
          publishedDate: new Date(news.publishedDate).toLocaleDateString('zh-TW')
        }))
        .slice(0, 30); // 限制顯示30條新聞

      setNewsData(prev => ({
        ...prev,
        news: processedNews,
        isLoading: false,
        error: null
      }));

      // 保存數據到單一緩存（覆蓋舊資料）
      const cacheKey = `list_news_data_${selectedSymbol}`;
      const existing = getFromCacheE(cacheKey) || {};
      const payload = {
        symbol: selectedSymbol,
        news: processedNews,
        analysis: existing.analysis || null
      };
      saveExpiryToCache(cacheKey, payload, 10);
      logger.log(`新聞快取已寫入: ${cacheKey}, 條數=${processedNews.length}`);

    } catch (error) {
      logger.error('獲取新聞數據時發生錯誤:', error);
      setNewsData(prev => ({
        ...prev,
        error: '獲取新聞數據失敗',
        isLoading: false,
        news: []
      }));
    }
  };

  // 時間選項配置
  const timeOptions = [
    { value: 'today', label: '今天' },
    { value: 'three_days', label: '過去三天' },
    { value: 'one_week', label: '過去一週' },
    { value: 'one_month', label: '過去一個月' },
    { value: 'three_months', label: '過去三個月' }
  ];

  // 分析類型選項配置
  const typeOptions = [
    { value: 'rise', label: '大漲原因' },
    { value: 'drop', label: '大跌原因' },
    { value: 'industry_news', label: '所處產業的重要訊息' },
    { value: 'competitor_actions', label: '競爭對手的動作' },
    { value: 'analyst_ratings', label: '分析師升降評或調整目標價之理由' },
    { value: 'custom_question', label: '自訂問題' }
  ];

  // 獲取分析標題
  const getAnalysisTitle = (time, type) => {
    const timeText = timeOptions.find(opt => opt.value === time)?.label || '';
    const typeText = typeOptions.find(opt => opt.value === type)?.label || '';
    return `${timeText}${typeText}分析`;
  };

  // 選擇時間選項
  const handleTimeSelect = (time) => {
    setSelectedTime(time);
  };

  // 選擇分析類型
  const handleTypeSelect = (type) => {
    setSelectedType(type);
  };

  // 清除選擇
  const clearSelection = () => {
    setSelectedTime('');
    setSelectedType('');
  };

  // 清除時間選擇
  const clearTimeSelection = () => {
    setSelectedTime('');
  };

  // 清除類型選擇
  const clearTypeSelection = () => {
    setSelectedType('');
  };

  // 統一分析函數
  const handleAnalysis = async () => {
    const selectedSymbol = companyInfo?.symbol;
    const selectedCompanyName = companyInfo?.company_name;


    if (!selectedSymbol) {
      setAnalysisResult(prev => ({
        ...prev,
        error: '請選擇公司',
        isLoading: false
      }));
      return;
    }

    if (!selectedTime || !selectedType) {
      setAnalysisResult(prev => ({
        ...prev,
        error: '請選擇時間範圍和分析類型',
        isLoading: false
      }));
      return;
    }

    // 檢查分析快取 - 如果有緩存且當前沒有顯示分析結果，則顯示緩存
    const analysisKey = `list_news_data_${selectedSymbol}`;
    try {
      const cachedUnified = getFromCacheE(analysisKey);
      if (cachedUnified && 
          cachedUnified.symbol === selectedSymbol && 
          cachedUnified.analysis && 
          cachedUnified.analysis.html &&
          !analysisResult.isVisible) {
        setAnalysisResult({
          isLoading: false,
          error: null,
          result: cachedUnified.analysis.html || '',
          isVisible: true,
          type: `${selectedTime}_${selectedType}`
        });
        return;
      }
    } catch (_) {}

    // AI 使用次數檢查
    const limitCheck = await checkAILimit('AI_News');
    if (!limitCheck.canProceed) {
      setAnalysisResult(prev => ({
        ...prev,
        error: limitCheck.reason || 'AI 使用次數已達上限',
        isLoading: false
      }));
      return;
    }

    setAnalysisResult(prev => ({
      ...prev,
      isLoading: true,
      error: null,
      result: '',
      isVisible: true,
      type: `${selectedTime}_${selectedType}`
    }));

    try {
      // 獲取今天的日期
      const today = new Date();
      const todayString = today.toLocaleDateString('zh-TW', {
        year: 'numeric',
        month: '2-digit',
        day: '2-digit'
      });
      
      // 根據時間和類型設置不同的 prompt
      let timeText;
      switch(selectedTime) {
        case 'today':
          timeText = '今天';
          break;
        case 'three_days':
          timeText = '過去三天';
          break;
        case 'one_week':
          timeText = '過去一週';
          break;
        case 'one_month':
          timeText = '過去一個月';
          break;
        case 'three_months':
          timeText = '過去三個月';
          break;
        default:
          timeText = '今天';
      }

      let prompt;
      switch(selectedType) {
        case 'rise':
          prompt = `今天是 ${todayString}，請分析${timeText}美股 ${selectedCompanyName} 大漲的主要原因，包括正面消息、業績表現、市場情緒、技術面突破等推動股價上漲的關鍵因素，並說明這些因素如何影響投資者信心和股價走勢。`;
          break;
        case 'drop':
          prompt = `今天是 ${todayString}，請分析${timeText}美股 ${selectedCompanyName} 大跌的主要原因，包括負面消息、業績不佳、市場擔憂、技術面破位等導致股價下跌的關鍵因素，並說明這些因素如何影響投資者情緒和股價走勢。`;
          break;
        case 'industry_news':
          prompt = `今天是 ${todayString}，請統整${timeText}美股 ${selectedCompanyName} 所處產業的重要訊息，包括產業趨勢、政策變化、技術突破、市場需求變化等，並分析這些訊息對該公司的影響。`;
          break;
        case 'competitor_actions':
          prompt = `今天是 ${todayString}，請分析${timeText}美股 ${selectedCompanyName} 主要競爭對手的動作，包括新產品發布、併購活動、戰略合作、市場擴張等，並評估這些競爭動態對該公司的影響。`;
          break;
        case 'analyst_ratings':
          prompt = `今天是 ${todayString}，請統整${timeText}美股 ${selectedCompanyName} 分析師的升降評級和目標價調整，包括各大投行和研究機構的觀點，並分析這些評級變化的理由和對股價的潛在影響。`;
          break;
        default:
          prompt = `今天是 ${todayString}，請分析${timeText}美股 ${selectedCompanyName} 的市場表現和相關訊息。`;
      }

      // 若使用者有輸入自訂問題，將其附加到 prompt
      if (userQuestion && userQuestion.trim()) {
        prompt += ` 請一併回答使用者的自訂問題：「${userQuestion.trim()}」。`;
      }

      // 要求以 HTML 格式回覆，便於直接渲染
      prompt += '必要時加上表格，請用HTML格式回覆，使用適當的HTML標籤（如h3、p、ul、li、table等），並加上精簡的內聯樣式。';

      const messages = [
        {
          role: "system",
          content: "你是一個專業的股票分析師，請根據提供的股票代碼進行分析。請用繁體中文回答，並提供結構化的分析。重要：你的回覆必須直接使用HTML格式，不要使用Markdown格式。"
        },
        {
          role: "user",
          content: prompt
        }
      ];

      const response = await callAIProxyStream(messages, 'news');

      const reader = response.body.getReader();
      const decoder = new TextDecoder();
      let buffer = '';
      let rawAccumulated = '';

      let isDone = false;

      const filterThink = (str) =>
        str
          .replace(/<think>[\s\S]*?<\/think>/g, '')
          .replace(/<think>[\s\S]*$/, '');
      
      while (!isDone) {
        const { done, value } = await reader.read();
        if (done) break;

        const chunk = decoder.decode(value);
        buffer += chunk;
        
        const lines = buffer.split('\n');
        buffer = lines.pop() || '';

        for (const line of lines) {
          if (line.trim() === '') continue;
          if (line.trim() === 'data: [DONE]') {
            isDone = true;
            break;
          }

          try {
            const json = JSON.parse(line.replace('data: ', ''));
            if (json.error) throw new Error(json.error);
            const content = json.choices?.[0]?.delta?.content;
            if (content) {
              rawAccumulated += content;
              const filtered = filterThink(rawAccumulated);
              if (!filtered.trim()) continue;

              let displayContent = filtered;
              const htmlBlockMatch = filtered.match(/```html\s*([\s\S]*?)(?:```|$)/);
              if (htmlBlockMatch) displayContent = htmlBlockMatch[1].trim();

              setAnalysisResult(prev => ({
                ...prev,
                result: displayContent
              }));
            }
          } catch (e) {
            logger.warn('解析串流數據時出錯:', e);
          }
        }
      }

      const aggregated = filterThink(rawAccumulated);

      // 串流結束，寫入單一快取（覆蓋）
      if (aggregated) {
        // 提取 ```html 代码块中的内容
        let contentToSave = aggregated;
        const htmlBlockMatch = aggregated.match(/```html\s*([\s\S]*?)(?:```|$)/);
        if (htmlBlockMatch) {
          // 如果找到 ```html 代码块，只保存其中的内容
          contentToSave = htmlBlockMatch[1].trim();
          logger.log(`检测到 \`\`\`html 代码块，已提取其中的内容`);
        }
        
        const cacheKeyUnified = `list_news_data_${selectedSymbol}`;
        const existing = getFromCacheE(cacheKeyUnified) || {};
        const payload = {
          symbol: selectedSymbol,
          news: existing.news || [],
          analysis: {
            html: contentToSave,
            time: selectedTime,
            type: selectedType
          }
        };
        saveExpiryToCache(cacheKeyUnified, payload, 10);
        logger.log(`分析快取已寫入(單一鍵): ${cacheKeyUnified}, 長度=${contentToSave.length}`);
      }

      setAnalysisResult(prev => ({
        ...prev,
        isLoading: false
      }));

      // 記錄活動
      activityTracker.trackBtnExecution(`AI_News_${selectedTime}_${selectedType}_analysis`, selectedSymbol, `${selectedTime}_${selectedType}_analysis`);

    } catch (error) {
      logger.error(`${selectedTime}_${selectedType} 分析時發生錯誤:`, error);
      setAnalysisResult(prev => ({
        ...prev,
        error: `${timeOptions.find(opt => opt.value === selectedTime)?.label}${typeOptions.find(opt => opt.value === selectedType)?.label}分析失敗，請稍後再試`,
        isLoading: false
      }));
    }
  };



  // 處理新聞點擊
  const handleNewsClick = (url) => {
    window.open(url, '_blank', 'noopener,noreferrer');
  };

  return (
    <div className="news-container">
      <div className="content-container">
        <Card 
          title={
            <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
              <Title level={4}>
                {companyInfo?.symbol || '未知'} 相關新聞
              </Title>
            </div>
          } 
          className="news-card"
          bodyStyle={{ padding: '16px' }}
        >
          {/* AI 分析配置區域 */}
          <div className="analysis-config-section" style={{ marginBottom: '20px' }}>

            {/* 選項區域 */}
            <div style={{ display: 'flex', flexDirection: 'column', gap: '16px' }}>
              {/* 時間選項 */}
              <div>
                <div style={{ marginBottom: '8px', fontWeight: 'bold', color: '#262626' }}>
                  請選擇-新聞時間範圍：
                </div>
                <Space wrap>
                  {timeOptions.map(option => (
                    <Tag
                      key={option.value}
                      color={selectedTime === option.value ? 'blue' : 'default'}
                      style={{ 
                        cursor: 'pointer',
                        padding: '4px 8px',
                        fontSize: '13px'
                      }}
                      onClick={() => handleTimeSelect(option.value)}
                    >
                      {option.label}
                    </Tag>
                  ))}
                </Space>
              </div>
              
              {/* 分析類型選項 */}
              <div>
                <div style={{ marginBottom: '8px', fontWeight: 'bold', color: '#262626' }}>
                  請選擇-新聞分析類型：
                </div>
                <Space wrap>
                  {typeOptions.map(option => (
                    <Tag
                      key={option.value}
                      color={selectedType === option.value ? 'blue' : 'default'}
                      style={{ 
                        cursor: 'pointer',
                        padding: '4px 8px',
                        fontSize: '13px'
                      }}
                      onClick={() => handleTypeSelect(option.value)}
                    >
                      {option.label}
                    </Tag>
                  ))}
                </Space>
              </div>
            </div>
          
            <div style={{ display: 'flex', alignItems: 'center', gap: '10px', marginTop: '20px', marginBottom: '16px', flexWrap: 'wrap' }}>
              <span style={{ fontWeight: 'bold', color: '#262626' }}>已選擇：</span>
              
              {/* 時間選擇輸入框 */}
              <div style={{ display: 'flex', alignItems: 'center', gap: '2px' }}>
                <span style={{ fontSize: '14px', color: '#595959' }}> - </span>
                <Input
                  placeholder="請選擇時間範圍"
                  value={selectedTime ? timeOptions.find(opt => opt.value === selectedTime)?.label : ''}
                  readOnly
                  style={{ width: '100px' }}
                  suffix={
                    selectedTime && (
                      <CloseOutlined 
                        onClick={clearTimeSelection}
                        style={{ cursor: 'pointer', color: '#999' }}
                      />
                    )
                  }
                />
              </div>
              
              {/* 類型選擇輸入框 */}
              <div style={{ display: 'flex', alignItems: 'center', gap: '8px' }}>
                <span style={{ fontSize: '14px', color: '#595959' }}> - </span>
                <Input
                  placeholder="請選擇分析類型"
                  value={selectedType ? typeOptions.find(opt => opt.value === selectedType)?.label : ''}
                  readOnly
                  style={{ width: '260px' }}
                  suffix={
                    selectedType && (
                      <CloseOutlined 
                        onClick={clearTypeSelection}
                        style={{ cursor: 'pointer', color: '#999' }}
                      />
                    )
                  }
                />
              </div>
              
              {/* 自訂問題輸入框（可選） */}
              {selectedType === 'custom_question' && (
                <div style={{ display: 'flex', alignItems: 'center', gap: '8px' }}>
                  <span style={{ fontSize: '14px', color: '#595959' }}> - </span>
                  <Input
                    placeholder="想詢問的自訂問題（可選）"
                    value={userQuestion}
                    onChange={(e) => setUserQuestion(e.target.value)}
                    style={{ width: '320px' }}
                    allowClear
                  />
                </div>
              )}
              
              {/* 分析按鈕 */}
              <Button
                type="primary"
                icon={<RobotOutlined />}
                onClick={handleAnalysis}
                loading={analysisResult.isLoading}
                disabled={!companyInfo?.symbol || !selectedTime || !selectedType || analysisResult.isLoading}
                style={selectedTime && selectedType ? { 
                  backgroundColor: '#ff3a45', 
                  borderColor: '#ff3a45',
                  color: '#fff'
                } : {}}
              >
                搜尋
              </Button>
              
            </div>
            
          </div>
          {/* 統一分析結果區域 */}
          {analysisResult.isVisible && (
            <div className="analysis-section" style={{ marginBottom: '20px' }}>
              {analysisResult.error && (
                <Alert
                  message="分析錯誤"
                  description={analysisResult.error}
                  type="error"
                  showIcon
                  style={{ marginBottom: '16px' }}
                />
              )}
              
              {analysisResult.isLoading && (
                <div style={{ textAlign: 'center', padding: '20px' }}>
                  <Spin size="large" />
                  <div style={{ marginTop: '16px' }}>AI 正在分析中...</div>
                </div>
              )}
              
              {analysisResult.result && (
                <Card 
                  title={getAnalysisTitle(analysisResult.type.split('_')[0], analysisResult.type.split('_')[1])} 
                  size="small"
                  style={{ marginBottom: '16px' }}
                >
                  <div 
                    className="analysis-result"
                    style={{ 
                      lineHeight: '1.6',
                      fontSize: '14px'
                    }}
                    dangerouslySetInnerHTML={{ __html: analysisResult.result }}
                  />
                </Card>
              )}
            </div>
          )}

          {newsData.isLoading && (
            <div style={{ textAlign: 'center', padding: '40px' }}>
              <Spin size="large" />
              <div style={{ marginTop: '16px' }}>正在加載新聞...</div>
            </div>
          )}

          {newsData.error && (
            <Alert
              message="錯誤"
              description={newsData.error}
              type="error"
              showIcon
              style={{ marginBottom: '16px' }}
            />
          )}

          {!newsData.isLoading && !newsData.error && newsData.news.length === 0 && (
            <div style={{ textAlign: 'center', padding: '40px', color: '#666' }}>
              暫無相關新聞
            </div>
          )}

          {!newsData.isLoading && !newsData.error && newsData.news.length > 0 && (
            <Row gutter={[16, 16]}>
              {newsData.news.map((news, index) => (
                <Col 
                  key={index}
                  xs={24} 
                  sm={12} 
                  md={8} 
                  lg={8} 
                  xl={8}
                >
                  <Card
                    hoverable
                    className="news-item-card"
                    bodyStyle={{ padding: '12px' }}
                    onClick={() => handleNewsClick(news.url)}
                    style={{ cursor: 'pointer', height: '100%' }}
                  >
                    <div className="news-image-container">
                      <Image
                        src={news.image}
                        alt={news.title}
                        width="100%"
                        height={120}
                        style={{ objectFit: 'cover' }}
                        fallback="data:image/png;base64,iVBORw0KGgoAAAANSUhEUgAAAMIAAADDCAYAAADQvc6UAAABRWlDQ1BJQ0MgUHJvZmlsZQAAKJFjYGASSSwoyGFhYGDIzSspCnJ3UoiIjFJgf8LAwSDCIMogwMCcmFxc4BgQ4ANUwgCjUcG3awyMIPqyLsis7PPOq3QdDFcvjV3jOD1boQVTPQrgSkktTgbSf4A4LbmgqISBgTEFyFYuLykAsTuAbJEioKOA7DkgdjqEvQHEToKwj4DVhAQ5A9k3gGyB5IxEoBmML4BsnSQk8XQkNtReEOBxcfXxUQg1Mjc0dyHgXNJBSWpFCYh2zi+oLMpMzyhRcASGUqqCZ16yno6CkYGRAQMDKMwhqj/fAIcloxgHQqxAjIHBEugw5sUIsSQpBobtQPdLciLEVJYzMPBHMDBsayhILEqEO4DxG0txmrERhM29nYGBddr//5/DGRjYNRkY/l7////39v///y4Dmn+LgeHANwDrkl1AuO+pmgAAADhlWElmTU0AKgAAAAgAAYdpAAQAAAABAAAAGgAAAAAAAqACAAQAAAABAAAAwqADAAQAAAABAAAAwwAAAAD9b/HnAAAHlklEQVR4Ae3dP3Ik1RnG4W+FgYxN"
                        preview={false}
                      />
                    </div>
                    <div className="news-content">
                      <Title level={5} className="news-title">
                        {news.title}
                      </Title>
                      <Typography.Paragraph 
                        className="news-text" 
                        ellipsis={{ rows: 3, expandable: false }}
                        style={{ margin: 0 }}
                      >
                        {news.text}
                      </Typography.Paragraph>
                      <div className="news-meta">
                        <Text type="secondary" className="news-publisher">
                          {news.publisher}
                        </Text>
                        <Text type="secondary" className="news-date">
                          {news.publishedDate}
                        </Text>
                      </div>
                    </div>
                  </Card>
                </Col>
              ))}
            </Row>
          )}
        </Card>
        <AIUpgradePrompt
          visible={showUpgradePrompt}
          onCancel={() => setShowUpgradePrompt(false)}
          limitInfo={limitInfo}
        />
      </div>
      <style jsx>{`
        .news-container {
          padding: 20px;
          max-width: 1600px;
          width: 100%;
          margin: 0 auto;
          box-sizing: border-box;
          overflow-x: hidden;
        }
        
        .news-container *,
        .news-container *:before,
        .news-container *:after {
          max-width: 100%;
          box-sizing: border-box;
        }
        
        .news-container :global(.ant-card),
        .news-container :global(.ant-card-body),
        .news-container :global(.ant-row),
        .news-container :global(.ant-col) {
          width: 100% !important;
          max-width: 100% !important;
          box-sizing: border-box !important;
        }
        
        .content-container {
          width: 100%;
          max-width: 100%;
          box-sizing: border-box;
        }
        
        .news-card {
          background: #fff;
          border-radius: 8px;
          box-shadow: 0 2px 8px rgba(0, 0, 0, 0.1);
        }
        
        .news-item-card {
          transition: all 0.3s ease;
          border: 1px solid #f0f0f0;
          border-radius: 8px;
        }
        
        .news-item-card:hover {
          box-shadow: 0 4px 12px rgba(0, 0, 0, 0.15);
          transform: translateY(-2px);
        }
        
        .news-image-container {
          margin-bottom: 12px;
          border-radius: 6px;
          overflow: hidden;
        }
        
        .news-content {
          display: flex;
          flex-direction: column;
          height: calc(100% - 132px);
        }
        
        .news-title {
          margin-bottom: 8px !important;
          line-height: 1.4 !important;
          font-weight: 600 !important;
          color: #262626 !important;
        }
        
        .news-text {
          flex: 1;
          color: #595959 !important;
          line-height: 1.5 !important;
          margin-bottom: 8px !important;
        }
        
        .news-meta {
          display: flex;
          justify-content: space-between;
          align-items: center;
          margin-top: auto;
          font-size: 12px;
        }
        
        .news-publisher {
          font-weight: 500;
        }
        
        .news-date {
          color: #8c8c8c !important;
        }

        /* 統一分析區域樣式 */
        .analysis-section {
          border: 1px solid #f0f0f0;
          border-radius: 8px;
          background: #fafafa;
          padding: 16px;
        }
        
        .analysis-result {
          color: #262626;
          font-family: -apple-system, BlinkMacSystemFont, 'Segoe UI', Roboto, 'Helvetica Neue', Arial, sans-serif;
        }
        
        .analysis-result h1, .analysis-result h2, .analysis-result h3 {
          color: #1890ff;
          margin-top: 16px;
          margin-bottom: 8px;
        }
        
        .analysis-result p {
          margin-bottom: 8px;
        }
        
        .analysis-result ul, .analysis-result ol {
          margin-left: 20px;
          margin-bottom: 8px;
        }

        /* 響應式設計 */
        @media screen and (max-width: 1600px) {
          .news-container {
            max-width: 100%;
          }
        }
        
        @media screen and (max-width: 768px) {
          .news-container {
            padding: 15px;
          }
          
          .news-item-card {
            margin-bottom: 12px;
          }
          
          .news-title {
            font-size: 14px !important;
          }
          
          .news-text {
            font-size: 13px !important;
          }
          
          .analysis-config-section > div:first-child {
            flex-direction: column;
            align-items: flex-start;
            gap: 12px;
          }
          
          .analysis-config-section > div:first-child > div {
            width: 100%;
            justify-content: flex-start;
          }
        }

        @media screen and (max-width: 480px) {
          .news-container {
            padding: 10px;
          }
          
          .news-item-card {
            margin-bottom: 8px;
          }
          
          .news-title {
            font-size: 13px !important;
          }
          
          .news-text {
            font-size: 12px !important;
          }
          
          .news-meta {
            font-size: 11px;
          }
          
          .analysis-config-section > div:first-child {
            flex-direction: column;
            align-items: flex-start;
            gap: 8px;
          }
          
          .analysis-config-section > div:first-child > div {
            width: 100%;
            justify-content: flex-start;
            flex-wrap: wrap;
          }
          
          .analysis-config-section > div:first-child > div > span {
            font-size: 12px;
          }
          
          .analysis-config-section > div:first-child > div > input {
            font-size: 12px;
            padding: 4px 8px;
          }
          
          .analysis-config-section > div:first-child > div > button {
            font-size: 12px;
            padding: 4px 8px;
            min-width: auto;
          }
        }
      `}</style>
    </div>
  );
}

export default ListNews; 